/**
 * Parques propios: depósito y alquiler.
 *
 * Dos parques de extintores de la empresa con finalidades distintas:
 *
 * - **Depósito**: se dejan en casa del cliente mientras los suyos están en
 *   el taller. Recoger para revisar sin dejar sustitución es dejar el centro
 *   desprotegido.
 * - **Alquiler**: van a eventos unos días y vuelven.
 *
 * La pantalla pregunta por unas **fechas** antes de decir nada, y no es un
 * capricho: un extintor está libre *para algo*. Sin período, «disponible» no
 * significa nada — y el que está libre hoy puede estar comprometido el fin
 * de semana.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { DetailShell, type Tab } from './detail/detail-shell.js';

interface Unit {
  equipmentId: string;
  internalCode: string;
  equipmentTypeCode: string;
  status: string;
  retestDueOn: string | null;
  lifeEndsOn: string | null;
  reason?: string;
}

interface PoolData {
  pool: string;
  period: { startsOn: string; endsOn: string };
  /** Cuántas unidades hay en el parque. Un recuento, no un importe. */
  unitCount: number;
  available: Unit[];
  unavailable: Unit[];
}

/** Hoy y dentro de tres días: el período típico de un evento. */
function porDefecto(): { from: string; to: string } {
  const hoy = new Date();
  const fin = new Date(hoy.getTime() + 3 * 86_400_000);
  return { from: hoy.toISOString().slice(0, 10), to: fin.toISOString().slice(0, 10) };
}

function Pool({ pool, ayuda }: { pool: 'deposito' | 'alquiler'; ayuda: string }): React.ReactNode {
  const { api } = useSession();
  const inicial = porDefecto();
  const [from, setFrom] = useState(inicial.from);
  const [to, setTo] = useState(inicial.to);
  const [data, setData] = useState<PoolData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get<PoolData>(`/pools/${pool}?from=${from}&to=${to}`));
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar el parque.');
    } finally {
      setLoading(false);
    }
  }, [api, pool, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <p className="screen__note">{ayuda}</p>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}

      <div className="field-grid">
        <label>
          Desde
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          Hasta
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          <small className="field__help">
            La disponibilidad es para estas fechas, no para hoy.
          </small>
        </label>
      </div>

      {loading && <p className="table__loading">Comprobando disponibilidad…</p>}

      {data !== null && (
        <>
          <div className="figures">
            <div className="figure">
              <span className="figure__value">{data.unitCount}</span>
              <span className="figure__label">en el parque</span>
            </div>
            <div className="figure">
              <span className="figure__value">{data.available.length}</span>
              <span className="figure__label">libres estas fechas</span>
            </div>
            <div className={`figure${data.unavailable.length > 0 ? ' figure--warn' : ''}`}>
              <span className="figure__value">{data.unavailable.length}</span>
              <span className="figure__label">no disponibles</span>
            </div>
          </div>

          {data.unitCount === 0 && (
            <p className="table__empty">
              El parque está vacío. Los extintores propios se asignan desde su ficha, marcándolos
              como equipo de la empresa y eligiendo el parque.
            </p>
          )}

          {data.available.length > 0 && (
            <>
              <h3>Libres</h3>
              <div className="scroller">
                <table className="table__grid">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Tipo</th>
                      <th>Retimbrado</th>
                      <th>Vida útil</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.available.map((u) => (
                      <tr key={u.equipmentId}>
                        <td>{u.internalCode}</td>
                        <td>{u.equipmentTypeCode}</td>
                        <td>{u.retestDueOn ?? '—'}</td>
                        <td>{u.lifeEndsOn ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {data.unavailable.length > 0 && (
            <>
              <h3>No disponibles</h3>
              {/* El motivo, no sólo el hecho: faltar extintores por no
                  tenerlos o por tenerlos ocupados son problemas distintos,
                  uno se arregla comprando y el otro cambiando la fecha. */}
              <p className="field__help">
                Con el motivo de cada uno: no es lo mismo que falten porque no se tienen que porque
                están comprometidos.
              </p>
              <div className="scroller">
                <table className="table__grid">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Tipo</th>
                      <th>Por qué no</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.unavailable.map((u) => (
                      <tr key={u.equipmentId}>
                        <td>{u.internalCode}</td>
                        <td>{u.equipmentTypeCode}</td>
                        <td className="due--late">{u.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

export function PoolsScreen(): React.ReactNode {
  const tabs: Tab[] = [
    {
      id: 'deposito',
      label: 'Depósito',
      render: () => (
        <Pool
          pool="deposito"
          ayuda="Extintores propios que se dejan en casa del cliente mientras los suyos están en el taller. El §41 no lo pide por comodidad: recoger para revisar sin dejar sustitución es dejar el centro desprotegido."
        />
      ),
    },
    {
      id: 'alquiler',
      label: 'Alquiler',
      render: () => (
        <Pool
          pool="alquiler"
          ayuda="Extintores propios que van a eventos unos días y vuelven. Se facturan por unidad y día, con el mínimo de su tarifa."
        />
      ),
    },
  ];

  return (
    <DetailShell
      title="Parques propios"
      subtitle="Extintores de la empresa: los que se dejan en depósito y los que se alquilan"
      tabs={tabs}
    />
  );
}
